import { useEffect, Suspense, lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { useAuthStore } from './stores/authStore'
import { useUIStore } from './stores/uiStore'
import LoadingSpinner from './components/common/LoadingSpinner'
import { Toaster } from './components/ui/toaster'

// Lazy load components for code splitting
const LandingPage = lazy(() => import('./pages/LandingPage'))
const AboutPage = lazy(() => import('./pages/AboutPage'))
const MobileAppPage = lazy(() => import('./pages/MobileAppPage'))
const LoginPage = lazy(() => import('./pages/auth/LoginPage'))
const RequestAccessPage = lazy(() => import('./pages/public/RequestAccessPage'))
const AdminLayout = lazy(() => import('./components/layout/AdminLayout'))
const ParentLayout = lazy(() => import('./components/layout/ParentLayout'))
const StudentLayout = lazy(() => import('./components/layout/StudentLayout'))

function App() {
  const { isAuthenticated, isLoading, user, initialize } = useAuthStore()
  const { loading } = useUIStore()

  useEffect(() => {
    initialize()
  }, [initialize])

  if (isLoading || loading) {
    return <LoadingSpinner size="lg" text="Initializing MindSpire Planner..." />
  }

  if (!isAuthenticated) {
    return (
      <HelmetProvider>
        <Suspense fallback={<LoadingSpinner size="md" text="Loading..." showLogo={false} />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/mobile" element={<MobileAppPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/request-access" element={<RequestAccessPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <Toaster />
      </HelmetProvider>
    )
  }

  // Role-based routing
  return (
    <HelmetProvider>
      <Suspense fallback={<LoadingSpinner size="md" text="Loading..." showLogo={false} />}>
        <Routes>
          {user?.role === 'ADMIN' && (
            <Route path="/admin/*" element={<AdminLayout />} />
          )}
          {user?.role === 'PARENT' && (
            <Route path="/parent/*" element={<ParentLayout />} />
          )}
          {user?.role === 'STUDENT' && (
            <Route path="/student/*" element={<StudentLayout />} />
          )}
          
          {/* Default redirects based on role */}
          <Route 
            path="/" 
            element={
              <Navigate 
                to={user?.role === 'ADMIN' ? '/admin' : user?.role === 'PARENT' ? '/parent' : '/student'} 
                replace 
              />
            } 
          />
          
          {/* Catch all - redirect to appropriate dashboard */}
          <Route 
            path="*" 
            element={
              <Navigate 
                to={user?.role === 'ADMIN' ? '/admin' : user?.role === 'PARENT' ? '/parent' : '/student'} 
                replace 
              />
            } 
          />
        </Routes>
      </Suspense>
      <Toaster />
    </HelmetProvider>
  )
}

export default App
